import { Avatar, Box, Typography } from "@mui/material";
import PropTypes from "prop-types";
import { useTheme } from "../contexts/ThemeContext";
import { resolveImageUrl } from "../utils/imageUrl";

/**
 * ProjectIcon component - displays project icon image or first letter of project name
 * @param {string} name - The project name to display the first letter of
 * @param {string} icon - The project icon URL (optional)
 * @param {number} size - The size of the icon box (width and height in pixels)
 * @param {number} fontSize - The font size of the letter (optional, defaults based on size)
 * @param {object} sx - Additional MUI sx prop styles (optional)
 */
export const ProjectIcon = ({ name, icon, size = 44, fontSize, sx = {} }) => {
  const defaultFontSize = size === 36 ? "14px" : "inherit";
  const { colors } = useTheme();

  // If icon exists, display it as an image
  if (icon) {
    return (
      <Avatar
        src={resolveImageUrl(icon)}
        alt={name}
        variant="rounded"
        sx={{
          width: size,
          height: size,
          borderRadius: size === 36 ? 1.5 : 2,
          border: "1px solid",
          borderColor: colors.border.primary,
          flexShrink: 0,
          ...sx,
        }}
      >
        {/* Fallback to first letter if image fails to load */}
        <Typography
          variant={size === 36 ? "body2" : "h5"}
          component="span"
          sx={{
            fontWeight: 600,
            color: "text.primary",
            mb: 0,
            fontSize: fontSize || defaultFontSize,
            lineHeight: size === 36 ? 1 : undefined,
          }}
        >
          {name?.charAt(0).toUpperCase() || "?"}
        </Typography>
      </Avatar>
    );
  }

  // Default: display first letter
  return (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: size === 36 ? 1.5 : 2,
        backgroundColor: "action.selected",
        border: "1px solid",
        borderColor: colors.border.secondary,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        ...sx,
      }}
    >
      <Typography
        variant={size === 36 ? "body2" : "h5"}
        component="span"
        sx={{
          fontWeight: 600,
          color: "text.primary",
          mb: 0,
          fontSize: fontSize || defaultFontSize,
          lineHeight: size === 36 ? 1 : undefined,
        }}
      >
        {name?.charAt(0).toUpperCase() || "?"}
      </Typography>
    </Box>
  );
};

ProjectIcon.propTypes = {
  name: PropTypes.string,
  icon: PropTypes.string,
  size: PropTypes.number,
  fontSize: PropTypes.string,
  sx: PropTypes.object,
};
